import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { flowMotionAtom } from '../atoms/flowMotionAtom';
import { flowSayTextAtom } from '../atoms/flowSayTextAtom';
import { flowTofAtom } from '../atoms/flowTofAtom';
import {
  DEFAULT_FPS,
  MOTION_LIBRARY_URL,
  createStandbyTransitionTimeline,
  createStandbyTimeline,
  getMotionDurationMs,
  getMotionAudioCues,
  getSoundKeyframesBetween,
  getTimelineDurationMs,
  getTimelineFrameAtElapsed,
  motionToTimeline,
  parseMotionLibrary,
  type WegoMotion,
} from '../animation/animationUtils';
import {
  useWegoAnimationScene,
  type BodyRotationAxis,
  type MazeCameraRotation,
  type MazeCameraMode,
  type MazeRobotPose,
  type SelectedWegoJoint,
  type WegoJointControl,
} from '../animation/useWegoAnimationScene';
import type { MazeStageDefinition } from '../config/mazeStages';
import { DEFAULT_EYE_LED_COLOR } from '../animation/eyeLedState';
import { getSimulationBgm, playSimulationBgm, playSimulationMotionAudio, playSimulationSoundEffect } from '../simulationSoundPlayer';

const LIBRARY_TIMEOUT_MS = 10_000;
const PREVIEW_FPS = 24;
const PREVIEW_FRAME_INTERVAL_MS = 1000 / PREVIEW_FPS;
const EYE_LED_BLINK_INTERVAL_MS = 1000;

type VirtualRobotProps = {
  /** プレビューが非表示のあいだ描画を止める。 */
  paused?: boolean;
  /** 発表表示用の白背景・床なしシーンを使う。 */
  presentationMode?: boolean;
  /** ドラッグで関節を動かせるようにする。 */
  jointDragEnabled?: boolean;
  onJointSelectionChange?: (joint: SelectedWegoJoint | null) => void;
  onJointValueControlReady?: (setDegrees: (degrees: number) => void) => void;
  onJointControlsChange?: (joints: WegoJointControl[]) => void;
  onJointControlReady?: (setValue: (jointId: string, value: number) => void) => void;
  onJointPoseReady?: (setValue: (jointId: string, value: number) => void) => void;
  onBodyRotationControlReady?: (setValue: (axis: BodyRotationAxis, degrees: number) => void) => void;
  onBodyRotationChange?: (rotation: Record<BodyRotationAxis, number>) => void;
  /** 現在の姿勢を背景透過 PNG として取得する関数を受け取る。 */
  onSnapshotReady?: (captureSnapshot: () => string | null) => void;
  onMotionElapsedChange?: (elapsedMs: number) => void;
  eyeLedColor?: string;
  eyeLedBlinking?: boolean;
  physicsEnabled?: boolean;
  showCollisionBoxes?: boolean;
  mazeStage?: MazeStageDefinition;
  mazeRobotPose?: MazeRobotPose;
  topView?: boolean;
  cameraMode?: MazeCameraMode;
  mazeCameraRotation?: MazeCameraRotation;
  onTofMeasureReady?: (measure: (() => number) | null) => void;
};

export const VirtualRobot = memo(function VirtualRobot({
  paused = false,
  presentationMode = false,
  jointDragEnabled = false,
  onJointSelectionChange,
  onJointValueControlReady,
  onJointControlsChange,
  onJointControlReady,
  onJointPoseReady,
  onBodyRotationControlReady,
  onBodyRotationChange,
  onSnapshotReady,
  onMotionElapsedChange,
  eyeLedColor,
  eyeLedBlinking = false,
  physicsEnabled = false,
  showCollisionBoxes = false,
  mazeStage,
  mazeRobotPose,
  topView = false,
  cameraMode = 'free',
  mazeCameraRotation = { horizontal: 0, vertical: 0 },
  onTofMeasureReady,
}: VirtualRobotProps) {
  const flowMotion = useAtomValue(flowMotionAtom);
  const flowSayText = useAtomValue(flowSayTextAtom);
  const flowTof = useAtomValue(flowTofAtom);
  const [motionLibrary, setMotionLibrary] = useState<WegoMotion[]>([]);
  const [libraryStatus, setLibraryStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [sceneStatus, setSceneStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [sceneError, setSceneError] = useState<string | null>(null);
  const [speech, setSpeech] = useState<string | null>(null);
  const playbackRafRef = useRef<number | null>(null);
  const motionAudioControllerRef = useRef<AbortController | null>(null);
  const speechTimerRef = useRef<number | null>(null);
  const previousMotionRequestIdRef = useRef<number | null>(null);
  const previousSpeechRequestIdRef = useRef<number | null>(null);
  const motionCompleteRef = useRef<(() => void) | null>(null);
  const preserveCompletedPoseRef = useRef(false);
  const speechCompleteRef = useRef<(() => void) | null>(null);
  const flowMotionRef = useRef(flowMotion);
  flowMotionRef.current = flowMotion;
  const motionElapsedRef = useRef(onMotionElapsedChange);
  motionElapsedRef.current = onMotionElapsedChange;

  const handleSceneReady = useCallback(() => {
    setSceneStatus('ready');
    setSceneError(null);
  }, []);

  const handleSceneError = useCallback((message: string) => {
    setSceneStatus('error');
    setSceneError(message);
  }, []);

  const {
    containerRef,
    loadTimeline,
    showFrame,
    setPaused,
    setSelectedJointDegrees,
    setJointValue,
    setJointPoseValue,
    setBodyRotation,
    setEyeLedColor,
    setMazeRobotPose,
    setMazeCameraMode,
    setMazeCameraRotation,
    measureTofDistanceMm,
    captureSnapshot,
  } = useWegoAnimationScene({
    onLoadError: handleSceneError,
    onSceneReady: handleSceneReady,
    jointDragEnabled,
    onJointSelectionChange,
    onJointControlsChange,
    onBodyRotationChange,
    physicsEnabled: physicsEnabled || flowMotion?.physicsEnabled === true,
    showCollisionBoxes,
    tofScanActive: Boolean(flowTof),
    tofScanDistanceMm: flowTof?.value ?? 0,
    mazeStage,
    topView,
    cameraMode,
    presentationMode,
  });

  useEffect(() => {
    if (mazeRobotPose) setMazeRobotPose(mazeRobotPose, Boolean(flowMotionRef.current));
  }, [mazeRobotPose, setMazeRobotPose]);

  useEffect(() => {
    setMazeCameraMode(cameraMode);
  }, [cameraMode, setMazeCameraMode]);

  useEffect(() => {
    setMazeCameraRotation(mazeCameraRotation);
  }, [mazeCameraRotation, setMazeCameraRotation]);

  useEffect(() => {
    onTofMeasureReady?.(measureTofDistanceMm);
    return () => onTofMeasureReady?.(null);
  }, [measureTofDistanceMm, onTofMeasureReady]);

  useEffect(() => {
    onJointValueControlReady?.(setSelectedJointDegrees);
  }, [onJointValueControlReady, setSelectedJointDegrees]);

  useEffect(() => {
    onJointControlReady?.(setJointValue);
  }, [onJointControlReady, setJointValue]);

  useEffect(() => {
    onJointPoseReady?.(setJointPoseValue);
  }, [onJointPoseReady, setJointPoseValue]);

  useEffect(() => {
    onBodyRotationControlReady?.(setBodyRotation);
  }, [onBodyRotationControlReady, setBodyRotation]);

  useEffect(() => {
    if (sceneStatus !== 'ready') return;
    onSnapshotReady?.(captureSnapshot);
  }, [captureSnapshot, onSnapshotReady, sceneStatus]);

  useEffect(() => {
    setPaused(paused);
  }, [paused, setPaused]);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), LIBRARY_TIMEOUT_MS);
    void fetch(MOTION_LIBRARY_URL, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<unknown>;
      })
      .then((value) => {
        if (!mounted) return;
        const motions = parseMotionLibrary(value);
        setMotionLibrary(motions);
        setLibraryStatus('ready');
        setLibraryError(null);
      })
      .catch(() => {
        if (!mounted) return;
        setLibraryStatus('error');
        setLibraryError('モーションデータを読み込めませんでした。');
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      mounted = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  const stopPlayback = useCallback(() => {
    if (playbackRafRef.current !== null) cancelAnimationFrame(playbackRafRef.current);
    playbackRafRef.current = null;
    motionAudioControllerRef.current?.abort();
    motionAudioControllerRef.current = null;
  }, []);

  useEffect(() => {
    if (!flowMotion) {
      stopPlayback();
      motionCompleteRef.current?.();
      motionCompleteRef.current = null;
      previousMotionRequestIdRef.current = null;
      if (!preserveCompletedPoseRef.current) loadTimeline(createStandbyTimeline());
      return;
    }
    if (
      previousMotionRequestIdRef.current === flowMotion.requestId
      || (!flowMotion.timeline && libraryStatus === 'loading')
      || sceneStatus === 'loading'
    ) return;
    previousMotionRequestIdRef.current = flowMotion.requestId;

    if (sceneStatus === 'error' || (!flowMotion.timeline && libraryStatus === 'error')) {
      if (flowMotion.onError) flowMotion.onError(new Error('VirtualRobotを再生できません。モデルとモーションの読み込みを確認してください。'));
      else flowMotion.onComplete?.();
      return;
    }
    const motion = flowMotion.timeline ? null : motionLibrary.find((candidate) => candidate.motionNo === flowMotion.motionNo);
    if (!flowMotion.timeline && !motion) {
      setLibraryError(`モーション ${flowMotion.motionNo} が見つかりません。`);
      if (flowMotion.onError) flowMotion.onError(new Error(`モーション ${flowMotion.motionNo} が見つかりません。`));
      else flowMotion.onComplete?.();
      return;
    }
    setLibraryError(null);

    stopPlayback();
    const motionAudioController = new AbortController();
    motionAudioControllerRef.current = motionAudioController;
    preserveCompletedPoseRef.current = false;
    motionCompleteRef.current?.();
    motionCompleteRef.current = flowMotion.onComplete ?? null;
    const nextTimeline = flowMotion.timeline ?? motionToTimeline(motion!, DEFAULT_FPS);
    const durationMs = flowMotion.timeline
      ? getTimelineDurationMs(flowMotion.timeline)
      : getMotionDurationMs(motion!);
    const standbyTransition = createStandbyTransitionTimeline(nextTimeline);
    const standbyTransitionDurationMs = getTimelineDurationMs(standbyTransition);
    loadTimeline(nextTimeline);
    if (durationMs <= 0) {
      motionCompleteRef.current?.();
      motionCompleteRef.current = null;
      return;
    }

    const startedAt = performance.now();
    const bgm = flowMotion.playBgm && !flowMotion.timeline ? getSimulationBgm(flowMotion.motionNo) : undefined;
    let bgmStarted = false;
    const audioCues = flowMotion.playNativeAudio
      ? flowMotion.timeline?.nativeAudioCues ?? (motion ? getMotionAudioCues(motion) : [])
      : [];
    let nextAudioCue = 0;
    const channelControllers = new Map<string, AbortController>();
    motionAudioController.signal.addEventListener('abort', () => {
      channelControllers.forEach(controller => controller.abort());
    }, { once: true });
    let lastPreviewAt = Number.NEGATIVE_INFINITY;
    let previousSoundFrame = -1;
    let returningToStandby = false;
    const reportAudioError = (error: unknown) => {
      setLibraryError(`音声の再生に失敗しました: ${error instanceof Error ? error.message : String(error)}`);
    };
    const tick = (now: number) => {
      const elapsedMs = Math.min(durationMs, Math.max(0, now - startedAt));
      while (elapsedMs < durationMs && nextAudioCue < audioCues.length && audioCues[nextAudioCue].timeMs <= elapsedMs) {
        const cue = audioCues[nextAudioCue++];
        channelControllers.get(cue.kind)?.abort();
        const controller = new AbortController();
        channelControllers.set(cue.kind, controller);
        void playSimulationMotionAudio(cue, controller.signal).catch(reportAudioError);
      }
      if (bgm && !bgmStarted && elapsedMs >= bgm.startMs && elapsedMs < durationMs) {
        bgmStarted = true;
        void playSimulationBgm(flowMotion.motionNo, motionAudioController.signal).catch(reportAudioError);
      }
      if (!returningToStandby && (elapsedMs >= durationMs || now - lastPreviewAt >= PREVIEW_FRAME_INTERVAL_MS)) {
        lastPreviewAt = now;
        const currentFrame = getTimelineFrameAtElapsed(nextTimeline, elapsedMs, durationMs);
        showFrame(currentFrame);
        getSoundKeyframesBetween(nextTimeline.soundKeyframes, previousSoundFrame, currentFrame)
          .forEach(({ sound }) => {
            void playSimulationSoundEffect(sound, motionAudioController.signal).catch(reportAudioError);
          });
        previousSoundFrame = Math.max(previousSoundFrame, currentFrame);
        motionElapsedRef.current?.(elapsedMs);
      }
      if (elapsedMs >= durationMs) {
        if (!returningToStandby) {
          returningToStandby = true;
          if (bgm || audioCues.length) motionAudioController.abort();
          loadTimeline(standbyTransition);
        }
        const returnElapsedMs = Math.min(standbyTransitionDurationMs, now - startedAt - durationMs);
        if (returnElapsedMs >= standbyTransitionDurationMs || now - lastPreviewAt >= PREVIEW_FRAME_INTERVAL_MS) {
          lastPreviewAt = now;
          showFrame(getTimelineFrameAtElapsed(
            standbyTransition,
            returnElapsedMs,
            standbyTransitionDurationMs,
          ));
        }
        if (returnElapsedMs >= standbyTransitionDurationMs) {
          playbackRafRef.current = null;
          preserveCompletedPoseRef.current = true;
          motionCompleteRef.current?.();
          motionCompleteRef.current = null;
          return;
        }
      }
      playbackRafRef.current = requestAnimationFrame(tick);
    };
    playbackRafRef.current = requestAnimationFrame(tick);
    return stopPlayback;
  }, [flowMotion, libraryStatus, loadTimeline, motionLibrary, sceneStatus, showFrame, stopPlayback]);

  useEffect(() => {
    if (sceneStatus !== 'ready' || libraryStatus !== 'ready' || !eyeLedColor) return;
    setEyeLedColor(eyeLedColor);
    if (!eyeLedBlinking || eyeLedColor === DEFAULT_EYE_LED_COLOR) return;

    const turnOffTimerId = window.setTimeout(() => {
      setEyeLedColor(DEFAULT_EYE_LED_COLOR);
    }, EYE_LED_BLINK_INTERVAL_MS);
    return () => window.clearTimeout(turnOffTimerId);
  }, [eyeLedBlinking, eyeLedColor, libraryStatus, sceneStatus, setEyeLedColor]);

  useEffect(() => {
    if (!flowSayText) {
      if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
      speechCompleteRef.current?.();
      speechCompleteRef.current = null;
      previousSpeechRequestIdRef.current = null;
      setSpeech(null);
      return;
    }
    if (previousSpeechRequestIdRef.current === flowSayText.requestId) return;
    previousSpeechRequestIdRef.current = flowSayText.requestId;
    if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
    speechCompleteRef.current?.();
    speechCompleteRef.current = flowSayText.onComplete ?? null;
    setSpeech(flowSayText.text);
    speechTimerRef.current = window.setTimeout(() => {
      speechTimerRef.current = null;
      setSpeech(null);
      speechCompleteRef.current?.();
      speechCompleteRef.current = null;
    }, flowSayText.duration);
    return () => {
      if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
    };
  }, [flowSayText]);

  useEffect(() => () => {
    stopPlayback();
    if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
    motionCompleteRef.current?.();
    speechCompleteRef.current?.();
    motionCompleteRef.current = null;
    speechCompleteRef.current = null;
  }, [stopPlayback]);

  const error = sceneError ?? libraryError;
  return (
    <div
      className="virtual-robot"
      aria-label="バーチャルロボットのプレビュー。ドラッグで視点を回転し、ホイールまたはピンチで拡大縮小できます。"
    >
      <div ref={containerRef} className="virtual-robot__scene" />
      {(sceneStatus === 'loading' || libraryStatus === 'loading') && !error && (
        <div className="virtual-robot__status">3Dモデルを読み込み中</div>
      )}
      {error && <div className="virtual-robot__status virtual-robot__status--error" role="status">{error}</div>}
      {speech && <div className="virtual-robot__speech" role="status" aria-live="polite">{speech}</div>}
    </div>
  );
});
